import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, ChevronRight, Plus, Eye } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AddArticleDialog } from "@/components/AddArticleDialog";
import { useAdminAuth } from "@/components/AdminAuth";
import { useArticles } from "@/hooks/useArticles";

export interface BlogPost {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  image: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  canonicalUrl?: string;
}

export const Blog = () => {
  const [addArticleDialogOpen, setAddArticleDialogOpen] = useState(false);
  const { isAdminLoggedIn } = useAdminAuth();
  const { articles, loading, error, fetchArticles } = useArticles();

  // Charger les articles au démarrage
  useEffect(() => {
    fetchArticles(); // Articles publiés seulement
  }, []);

  return (
    <>
      <SEO
        title="Blog - Conseils et Astuces Châteaux Gonflables | Château de Clémentine"
        description="Découvrez nos conseils d'experts pour organiser des fêtes inoubliables avec nos châteaux gonflables. Guides, astuces et actualités à Rhode-Saint-Genèse."
        keywords="blog château gonflable, conseils fête enfant, organisation anniversaire, guide parents, Rhode-Saint-Genèse"
        canonicalUrl="https://chateaudeclementine.com/blog"
      />
      <Header />
      <main className="min-h-screen bg-gradient-sky">
        {/* Hero Section */}
        <section className="py-16 text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Blog & Conseils
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Découvrez nos conseils d'experts, astuces et guides pour organiser des fêtes inoubliables avec nos châteaux gonflables.
            </p>
            
            {/* Add Article Button - Only visible for admin */}
            {isAdminLoggedIn && (
              <div className="mb-8">
                <Button 
                  onClick={() => setAddArticleDialogOpen(true)}
                  variant="hero"
                  size="lg"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un Article
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Articles Grid */}
        <section className="pb-20">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Chargement des articles...</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl text-red-500">⚠️</span>
                  </div>
                  <h3 className="text-xl font-display font-bold mb-4 text-red-600">Erreur de chargement</h3>
                  <p className="text-muted-foreground mb-6">
                    {error.includes('Failed to fetch') 
                      ? "Problème de connexion. Vérifiez votre connexion internet et réessayez."
                      : "Une erreur est survenue lors du chargement des articles."}
                  </p>
                  <button 
                    onClick={() => window.location.reload()} 
                    className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <Card key={article.id} className="overflow-hidden shadow-card hover:shadow-playful transition-all duration-500 hover:scale-105 bg-white">
                    {/* Article Image */}
                    <div className="relative h-48 overflow-hidden bg-gradient-subtle">
                      <img 
                        src={article.image_url || '/default-article-image.webp'}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src = '/default-article-image.webp';
                        }}
                      />
                      {/* Gradient overlay for better readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                      
                      {/* Category Badge */}
                      <div className="absolute top-4 right-4">
                        <Badge variant="secondary" className="bg-white/95 text-primary font-bold px-3 py-1">
                          {article.category}
                        </Badge>
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <CardHeader className="p-0 mb-4">
                        <CardTitle className="text-xl font-display font-bold mb-2 line-clamp-2">
                          {article.title}
                        </CardTitle>
                        <CardDescription className="text-sm leading-relaxed line-clamp-3">
                          {article.subtitle}
                        </CardDescription>
                      </CardHeader>

                      {/* Article Meta */}
                      <div className="flex items-center text-xs text-muted-foreground mb-4 space-x-4">
                        <div className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          <span>{article.author}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                        <div className="flex items-center">
                          <Eye className="w-3 h-3 mr-1" />
                          <span>{article.read_time} min</span>
                        </div>
                      </div>

                      {/* Article Excerpt */}
                      <div className="mb-6">
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {article.content.substring(0, 150)}...
                        </p>
                      </div>

                      {/* Read More Button */}
                      <Link to={`/blog/${article.slug}`}>
                        <Button variant="outline" size="sm" className="w-full">
                          Lire l'article <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {/* Empty State - uniquement si pas d'erreur */}
            {!loading && !error && articles.length === 0 && (
              <div className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-6">
                    <Plus className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-display font-bold mb-4">Aucun article pour le moment</h3>
                  <p className="text-muted-foreground mb-6">
                    Commencez par ajouter votre premier article de blog pour partager vos conseils et astuces.
                  </p>
                  {isAdminLoggedIn && (
                    <Button 
                      onClick={() => setAddArticleDialogOpen(true)}
                      variant="hero"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un Article
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      
      <AddArticleDialog 
        open={addArticleDialogOpen}
        onOpenChange={setAddArticleDialogOpen}
      />
    </>
  );
};